import {
  MapboxFeature,
  NextPriceScheme,
  NextRegionScheme,
  ProductPackageWithPrice,
} from "@coast/utils"
import {
  GQLCourseForSummaryFragment,
  GQLProductType,
  GQLUsStateId,
  GQLProductPackage,
  GQLProductPackageWithPrice,
} from "./graphqlTypes"

export type ID = string

export interface DriversEdReview {
  text: string
  name: string
  location: string
}

export interface DriversEdReviews {
  [key: string]: DriversEdReview[]
}

export type User = {
  id?: string
  dob: number
  isInstructor?: boolean
  isAdmin?: boolean
  migratedTo?: {
    email: string
  }
  previousHours?: number
  previousDrivingHours?: number
  previousObservationHours?: number
  userName: string
  firstName: string
  middleName: string
  lastName: string
  hasNoMiddleName: boolean
  email: string
  preferredPronoun?: string
  licenseNumber: string
  billingName: string
  billingEmail: string
  licenseEligibilityDate?: number
  licenseIssueDate: number
  licenseExpirationDate: number
  signupSource?: string
  address: {
    address: string
    city: string
    state: string
    zipcode: string
    phone: string
    secondaryPhone?: string
  }
  smsOptIn?: boolean
  acceptTOS?: boolean
  marketingOptIn?: boolean
  isDeleted?: boolean
  readBill?: boolean
  enrollmentFormsComplete?: boolean
  facilityNumber?: string
  preferredName?: string
  timeZone?: string
  lastActive?: number
  dmvPreRegNumber?: string
  isRegionalManager?: boolean
  tags?: string[]
}

export type UserSearch = {
  id: string
  altEmail?: string
  altName?: string
  altPhone?: string
  email: string
  name: string
  phone?: string
  signupTimestamp: number
  isMigrated?: boolean
  isDeleted?: boolean
}

export type UserWithId = Omit<User, "id"> & {
  id: string
}

export type StudentObj = {
  name: string
  id: string
}

export type ApiResponse = {
  message: string
  status: number
}

export type API500Response = {
  message: string
  sentryEventId: string
}

export type LessonAlertsFetchResponse = {
  status: number
  message: string
}

export interface LessonDetails {
  startTime: number
  endTime: number
  drivingMinutes: number
  observationMinutes: number
  lessonType: LessonType
  price: number
  hasHomePickup?: boolean
  pickupLocation: string
  isShareable: boolean
  searchState: string
  locationId: string
  meetingPointId?: string
  actualDurationMinutes?: number
  actualDurationNote?: string
  pickupLocationCoords: Coordinates
}

export interface UserLesson {
  id: string
  details: LessonDetails
  instructor: Instructor
}

export interface UserLessonWithReview extends UserLesson {
  hasReview: boolean
}

export interface LessonStudentDetails {
  allowObserver: string
  drivingMinutes: number
  observationMinutes: number
  pickupDetails?: string
  dmvLocation?: string
  dmvConfirmationCode?: string
  drivingHistory?: string
  previousSchoolLessons?: string
}

export interface InstructorLesson {
  id: string
  details: LessonDetails
  instructor: Instructor
  students: {
    [studentId: string]: LessonStudentDetails
  }
}

export interface LessonDateTime {
  startTime: number
  endTime: number
}

type NoteBaseType = {
  id: string
  timestamp: number
  note: string
  isArchived?: boolean
  createdById: string
  createdByName: string
  isPinned: boolean
}

export type UserNote = {
  hideFromInstructorView?: boolean
} & NoteBaseType

export type UserCertificate = {
  certIssuanceDate: string
  certNumber: string
  certType: string
} & NoteBaseType

export type Review = {
  text: string
  date: number
  rating: number
  source: string
  status: string
  studentId: string
  studentName: string
  lessonId: string | null
}

export type StateMeetingPoint = {
  label: string
  description: string
  address: string
  id: string
}

export type StateConfig = {
  hasMeetingPoints: boolean
  hasHomePickup: boolean
  id: string
  shortName: string
  longName: string
  isShareableByDefault: boolean
  hasFixedLocations: boolean
  bookingLimit?: number
}

export enum PackageTag {
  Popular = "popular",
}

// The values here are a bit ambigious because they're used
// to build URLs, which we can't change, so we're pulling
// them forward fro previous types.
export enum CourseType {
  TeenClassroom = "classroom-drivers-ed",
  AdultClassroom = "adult-classroom-drivers-ed",
  TeenOnline = "drivers-ed",
  AdultOnline = "adult-drivers-ed",
  ParentTaughtOnline = "parent-taught",
  TrafficSchool = "traffic-school",
}

export enum EmploymentType {
  PartTime = "part-time",
  FullTime = "full-time",
  FullTimeFixed = "full-time-fixed",
}

export const EmploymentTypes: { [type in EmploymentType]: string } = {
  [EmploymentType.PartTime]: "Part time",
  [EmploymentType.FullTime]: "Full time",
  [EmploymentType.FullTimeFixed]: "Full time fixed",
}

export enum InstructorService {
  DrivingLessons = "driving-lessons",
  RoadTests = "road-tests",
  ClassroomInstruction = "classroom-instruction",
}

export const InstructorServices: { [type in InstructorService]: string } = {
  [InstructorService.DrivingLessons]: "Driving Lessons",
  [InstructorService.RoadTests]: "Road Tests",
  [InstructorService.ClassroomInstruction]: "Classroom Instruction",
}

export type InstructorPublic = {
  about: string
  displayName: string
  id: string
  isInactive?: boolean
  isTrainee?: boolean
  languages?: string
  portraitImage?: string
  region?: string
  superRegionId?: string
  slackUsername?: string
  state: string
  summary?: string
  timeZone: string
  title?: string
  userId: string
  wallpaperImage?: string
}

export type InstructorPrivate = {
  availsFixedByDefault?: boolean
  baseAddress?: string
  breakTimes?: number[]
  canManageOwnSchedule?: boolean
  canManageMeetingLocations?: boolean
  email: string
  employmentType?: EmploymentType
  firstName: string
  id?: string
  lastName: string
  licenseExpirationDate?: number | null
  licenseNumber: string
  managerId?: string
  maxBaseTravelMinutes: number
  maxBaseTravelMinutesOverride?: number
  maxBetweenTravelMinutes: number
  maxBetweenTravelMinutesOverride?: number
  nonTeachingPay: number
  payrollFirstName?: string
  payrollLastName?: string
  phoneNumber: string
  regionalManagerId: string
  regionalManagerName: string
  servicesProvided?: any // todo
  teachingPay: number
  doNotSyncHoursWithPayroll?: boolean
  doNotSyncRoadTestHoursWithPayroll?: boolean
  jobCodes: JobCode[]
  paylocityId: string
  hasRoadTestStipend?: boolean
  hasSignedBreakTimeWaiver?: boolean
}

export type Instructor = InstructorPublic & InstructorPrivate

export type PrivateLookup = {
  [key: string]: InstructorPrivate
}

export type LocalInstructorPrivate = InstructorPrivate & { fullName?: string }

export type LocalPrivateLookup = {
  [key: string]: LocalInstructorPrivate
}

export type GenericStringObj = {
  [key: string]: string
}

export enum EventPosition {
  Middle = "middle",
  First = "first",
}

type InstructorBaseEvent = {
  dayStr: string
  activity: string
  travelTime?: number
  otherTime?: number
  startTime: number
  endTime?: number
  type: string
  position: EventPosition
  teachingTime?: number
}

export type InstructorTravelEvent = InstructorBaseEvent & {
  breakStartTime: number | null
  breakEndTime: number | null
  travelDistance?: number
  log?: TimeLog
  estTravelTime?: number
  estTravelDistance?: number
}

export type InstructorLessonEvent = Omit<
  InstructorBaseEvent,
  "teachingTime"
> & {
  teachingTime: number
  lesson: InstructorLesson
}

export type DaysEvents = {
  [date: string]: {
    day: string
    events: (InstructorTravelEvent | InstructorLessonEvent)[]
    vehicleLocation?: string
  }
}

// TODO status and types should be enums
export type LegalAndPermissions = {
  contractStatus: string
  privateApproved: boolean
  transferStatus: string
  transferType: string
  autogenerated?: boolean
  isVoid?: boolean
  isMigrated?: boolean
}

export type UserCourseMeta = {
  enrolled: boolean
  isParentGuideOrdered?: boolean
  percentComplete?: number
  percentTestsPassed?: number
  state: string
  lectures?: string[]
  currentLecture?: string
  tests?: {
    [key: string]: {
      numCorrect: number
      totalQuestions: number
    }
  }
  hasCompletedNotary?: boolean
  isMarketingAssessmentComplete?: boolean
  courseId?: CourseID
  courseCompletionDate?: number
}

export type ConfirmInfo = {
  controlNumber: string
  firstName: string
  middleName?: string
  middleInit?: string // TODO: to be removed once switch to middleName
  lastName: string
  noMiddleName?: boolean
  dob: string
  timestamp: number
  sex: string
  branchLicenseNumber?: string // specific to TX
  roadAndVisionExams?: string // specific to TX
}

export type AddressInfo = {
  address: string
  city: string
  state: string
  zipcode: string
}

export type OrderResponse = {
  address?: {
    address: string
    city: string
    state: string
    zipcode: string
    phone: string
    dob: string
    timestamp: number
    userName: string
    firstName: string
    middleName?: string
    middleInit?: string // TODO: to be removed once switch to middleName
    lastName: string
  }
  shipping?: {
    complete: boolean
    shippingDetails: {
      amount: number
      label: string
    }
    shippingMethod: string
  }
  confirmInfo?: ConfirmInfo
  courseId?: CourseID
}

export type SurveyResponse = {
  best: string
  courseScore: string
  timestamp: number
  worst: string
}

export interface ReportCategory {
  id: string
  label: string
  description?: string
}

export interface StudentLessonReportCategoryRating {
  [categoryId: string]: number
}

export interface StudentLessonReportSends {
  [sendId: string]: {
    sentAt: number
    sentTo: string[]
    sentById: string
    comments: string
  }
}

export interface StudentLessonReport {
  userId: string
  lessonId: string
  lessonStartTime: number // kept here for sharing with signed out people
  timeZone: string
  createdAt: number
  updatedAt: number
  createdBy: string
  ratings?: StudentLessonReportCategoryRating
  comments?: string
  sends?: StudentLessonReportSends
}

export type StudentLessonReportStub = Omit<
  StudentLessonReport,
  "createdAt" | "updatedAt" | "createdBy"
> & {
  createdAt?: number
  updatedAt?: number
  createdBy?: string
}

export interface TransientSlot {
  id: string
  startTime: number
  endTime: number
  instructor: Partial<Instructor>
  lessonType: Omit<LessonType, "stateMeta">
  isShareable: boolean
  price: number
  packagePrices?: ProductPackageWithPrice[]
  locationId?: string
  pickupAddress?: string
  meetingPointId?: string
  searchAddress: string
  searchCity: string
  searchState: string
  updatedAt?: number
  isCustom?: boolean
  reservedFor?: {
    userId: string
  }
  note?: string
  isShared?: boolean
  lessonId?: string
}

export interface StateMeta {
  features: string[]
  title?: string
  description?: string
  icon?: React.ReactNode
  isDefaultLessonType?: boolean
  onlyService?: string[]
  additionalContentId?: string
  doShowInPicker?: boolean
}

export enum LessonTypeName {
  OneHour = "oneHour",
  Shared = "shared",
  Standard = "standard",
  DmvTest = "dmv-test",
  RoadTest = "road-test",
  RoadTestWithCar = "road-test-with-car",
  RoadTest15 = "road-test-15",
  RoadTest15WithCar = "road-test-15-with-car",
  PrepTest60 = "prep-test-60",
  RoadTest120 = "road-test-120",
  RoadTest180 = "road-test-180",
}

export interface LessonType {
  name: LessonTypeName
  label: string
  filterLabel?: string
  durationMinutes: number
  stateMeta: {
    [stateAbbrv: string]: StateMeta
  }
}

export enum MeetingType {
  Home = "home",
  MeetingPoint = "meet",
}

export interface Coordinates {
  lat: number
  lng: number
}

export interface SlotLocation {
  address: string
  lat: number
  lng: number
  n_late: number
  n_lng: number
  neighborhood: string
  meetingPointId: string
  meetingPointLabel: string
  distanceMiles: number
}

export interface SchedulerSlot {
  start_time: number
  end_time: number
  is_private: number
  provider: {
    id: string
  }
  location: SlotLocation
  is_unpaired: boolean
  appointment_id: string | null
}

export interface SchedulerReturn {
  warnings: string[]
  session_stats: {
    [key: string]: number
  }
  data: SchedulerSlot[]
}

// This is the slot after it's been transformed
// by the fetching process, from the scheduler.
export interface SlotToPick {
  date: number
  startTime: number
  endTime: number
  instructorId: string
  instructorDisplayName: string
  isPrivate: number
  location: SlotLocation
  lessonId?: string
  isUnpaired?: boolean
  timeZone: string
}

export interface SlotsData {
  [key: string]: SlotToPick[]
}

export interface StudentAccountAlert {
  id: string
  note: string
  timestamp: number
  createdById: string
  createdByName: string
  isPinned?: boolean
}

export interface GCSource {
  label: string
  zipcode: string
  usStateId: string
}

export type GCSources = {
  [key: string]: GCSource
}

export interface VoucherCard {
  source: string
  name: string
  amountDollars: string
  amountHours: string
  zipcode: string
}

export interface Voucher {
  amountDollars: number
  amountHours: number
  code: string
  name: string
  source: string
  timestamp: number
  zipcode: string
}

export interface Vehicle {
  id: string
  number: string
  make: string
  model: string
  state: string
  name: string
  plate: string
  parkingHub: string
  parkingHubId: string
  color: string
  instructorIds: string[]
  samsaraId: string
  opsManagerUserId?: string
}

type CenterLocation = {
  location: string
  locationCoords?: {
    lat: number
    lng: number
  }
}

export interface VehicleAvail {
  id?: string
  availId: string
  endTime: number
  instructor: {
    displayName: string
    id: string
  }
  startTime: number
  vehicleName: string
}

export interface SuperRegion {
  name: string
  state: string
  googleBusinessPageUrl: string
  id: string
  slackChannelId?: string
}

export interface PurchaseLog {
  id: string
  timestamp: number
  amountDollars: number
  minutesPurchased?: number
  description: string
  stripeChargeId?: string
  stripeCustomerId?: string
  category: string
  state: string
  isVoid?: boolean
  prevLogId?: string | null
  linkedLogId?: string | null
  items?: PurchaseItem[]
  itemsToRefund?: RefundItem[]
}

export type PurchaseLogForTable = PurchaseLog & {
  userId: string
  isHidden: boolean
  isVoided: boolean
  voidedTimestamp: number
  isRefunded: boolean
  isRefund: boolean
  prevTimestamp: number
  isPartiallyRefunded: boolean
  refundedTimestamp: number
}

export interface PurchaseSubItem {
  itemId: string
  id: string
  name: string
  description?: string
  type: GQLProductType
  quantity: number
  basePrice: number
}

export interface PurchaseItem {
  itemId: string
  id: string
  name: string
  description?: string
  type: GQLProductType
  quantity: number
  price: number
  priceSchemePrice: number
  products?: PurchaseSubItem[] | null
}

export function isPackage(
  item: PurchaseItem | PurchaseSubItem,
): item is PurchaseItem {
  return item.type === GQLProductType.Package
}

export function isProduct(
  item: PurchaseItem | PurchaseSubItem,
): item is PurchaseSubItem {
  return !isPackage(item)
}

interface RefundItem {
  itemId: string
  refundedQuantity: number
}

export interface ExpiredCredit {
  id: string
  durationMinutes: number
  observationMinutes?: number
  homePickups?: number
  note: string
  price: number
  startTime: number
  type: string
}

export interface UserNotification {
  lessonId?: string
  message: string
  notificationType: "postLesson" | "sameDay" | "twoDay"
  sendStatus: "success" | "failure" | "attempted"
  sentTo?: string
  timestamp: number
  errorMessage?: string
}

export interface UserBalance {
  drivingMinutesUsed: number
  observationMinutesUsed: number
  drivingMinutesPurchased: number
  observationMinutesPurchased: number
  drivingMinutesRemaining: number
  observationMinutesRemaining: number
  expirationDrivingMinutesUsed: number
  expirationObservationMinutesUsed: number
  homePickupsPurchased: number
  homePickupsUsed: number
  homePickupsRemaining: number
  homePickupsExpired: number
}

export interface InstructorAvailFormData {
  date: string
  startTime: string
  endTime: string
  isPrivate: boolean
  isFixedLocation: boolean
  fixedLocationId?: string
  vehicleId?: string
  centerAddress: string
  maxDurationMinutes: number
  note: string
  maxBaseTravelMinutes?: number
  maxBetweenTravelMinutes?: number
  locationAllowed: AvailLocationAllowed
  lessonTypeAllowed: LessonTypeName | "all"
}

export interface InstructorAvail {
  id?: string
  center: CenterLocation
  date: string
  endTime: number
  instructor: {
    displayName: string
    timeZone: string
    id?: string
  }
  isPrivate: boolean
  isFixedLocation?: boolean
  fixedLocationId?: string
  maxLessons?: number
  maxDurationMinutes?: number
  minLeadHours: number
  note: string
  startTime: number
  vehicle?: {
    id: string
    instructorId?: string
    number: string
    location?: string
  } | null
  maxBaseTravelMinutes?: number | null
  maxBetweenTravelMinutes?: number | null
  locationAllowed?: AvailLocationAllowed
  lessonTypeAllowed?: LessonTypeName | null
}

export type VehicleWithInstructor = {
  instructorName: string
} & Vehicle

export interface Isochrone {
  type: "FeatureCollection"
  features: MapboxFeature[]
}
export interface NextFullRegion extends NextRegionScheme {
  priceScheme?: NextPriceScheme
  superRegion?: NextSuperRegion
}

export interface NextSuperRegion {
  name: string
  state: string
}

export interface LocationCoordsCache {
  administrativeLevels: {
    level1long: string
    level1short: string
    level2long: string
    level2short: string
  }
  extra: {
    confidence: number
    googlePlaceId: string
    neighborhood: string
  }
  city: string
  country: string
  countryCode: string
  formattedAddress: string
  latitude: number
  longitude: number
  provider: string
  zipcode: string
}

export interface ReviewAggregates {
  1: number
  2: number
  3: number
  4: number
  5: number
  averageRating: number
  total: number
}

export interface ReviewsObject {
  id: string
  name: string
  rating: number
  text: string
  date: number
}

export interface ReviewsInfo {
  numReviews: number
  averageRating: number
  ratingCounts: number[]
}

// The object returned by Stripe actually has a lot more
// fields, but these are the ones we're using.
export interface StripeChargeResponse {
  amount: number
  customer: string
  id: string
  latest_charge: string
}

export interface PriceBreakdown {
  chargeAmount: number
  drivingMinutesUsed: number
}

export type LegalAndPermissionsRow = {
  label: string
  value: string
  doShow: boolean
  showCheck: boolean
  showWarning: boolean
}

export interface AddressPoint {
  address: string
  distanceMiles: number
  meetingPointLabel: string
  meetingPointId: string
  isFixedLocation?: boolean
}

export interface MeetingPointResponse {
  id: string
  label: string
  address: string
  distance_miles: number
}

export interface Product {
  itemId: string
  id: string
  name: string
  description?: string
  type: string
  quantity: number
  quantityString: string
  customPriceString?: string
  availableQuantity: number
  availableAmount: number
  price?: number
  products?: Subproduct[]
  customPrice?: number
}

export interface Subproduct {
  type: string
  itemId: string
  productId: string
  productName: string
  quantity: number
  quantityString: string
  availableQuantity: number
}

export type TrackingProduct = {
  hasCourse: boolean
  id: string
  title: string
}

export type TrackingOrderInfo = {
  conversionValue?: number
  amountDollars: number
  revenueType?: string
  deliveryDate?: string
  category: "Behind-the-Wheel Lesson" | "Online Course" | "Course Shipping"
}

export interface UsedReferralCode {
  referralCode: string
  fullReferralCode: string
  timestamp: number
  fulfillmentStatus: "pending" | "fulfilled"
}

export interface Geocode {
  lat: number
  lng: number
  city?: string
  state: string
  zipcode?: string
}

type CourseFeature = {
  head: string
  text: string
  icon: React.ReactNode
}

type PromoText = {
  text: string
  url?: string
}

export type CoursePromo = {
  bannerText?: string
  comparisonFeatures?: string[]
  courseId: CourseID
  courseOnPhone?: string
  courseType: CourseType
  features: CourseFeature[]
  hasDrivingLessons: boolean
  hasScholarship: boolean
  head: string
  headerPromoList: PromoText[]
  intendedAudience: string
  intendedAudienceMore: string
  isClassroomCourse?: boolean
  isExternalCourse?: boolean
  isFeaturedCourse?: boolean
  pageTitle: string
  practiceTests?: string
  steps?: string[]
  subhead: string
  syllabus: string[]
  regionIds?: string[]
  landingPageUrl?: string
}

export type ClassroomCourseSettings = {
  id: string
  title: string
  slug: string
  locationId: string
  maxEnrollees: number
  state: GQLUsStateId
}

export type TestLectures = { id: string; title: string }

export type InstructorMerged = Instructor & {
  fullName: string
  superRegionName?: string
  superRegionId?: string
  slackChannelId?: string
}

export type SchoolData = {
  schoolName?: string
  customName?: string
  schoolCity: string
  schoolState: string
  schoolZip?: string
  schoolPhone?: string
  schoolType: string
}

export interface UserFile {
  id: string
  label: string
  url: string
  createdAt: number
  createdById: string
  path: string
}

export type CourseSession = {
  sessionStart: number
  sessionEnd: number
}

export type UserCourseMetaWithId = UserCourseMeta & { courseId: CourseID }

export type CourseMetaWithDetails = UserCourseMetaWithId & {
  details: GQLCourseForSummaryFragment
}

export type UserLog = {
  action: string
  changedById: string
  changes: any
  note: string
  timestamp: number
  type: string
  userId: string
  id?: string
  lessonId?: string
  lessonTimestamp?: number
  changedByName?: string
}

export type TimeLog = {
  id?: string
  description: string
  duration: number
  endTime?: number
  logType: "drive"
  rateType: JobCode
  startTime: number
  distance?: number
}

export enum AvailLocationAllowed {
  Home = "home",
  MeetingPoint = "meetingPoint",
  Both = "both",
}

// These map to the courses in Dato. They're here because
// we don't have access to the Dato API on the landing pages.
export enum CourseID {
  CA_TRAFFIC_SCHOOL = "FuAMyv_tT-ubOUBAoOMD3w",
  CA_TEEN_CLASSROOM_FRESNO = "148990601",
  CA_TEEN_ONLINE = "90758166",
  CO_TEEN_CLASSROOM_ARVADA = "CgAbux3fRcS39AHa4Eh2Gw",
  CO_TEEN_CLASSROOM_LITTLETON = "f_Zd3_m-TlaiYyisSMYv0A",
  CO_TEEN_ONLINE = "90758168",
  IL_TEEN_ONLINE = "148239976",
  IN_TEEN_ONLINE = "121419226",
  MO_TEEN_ONLINE = "90758170",
  NV_TEEN_ONLINE = "90758167",
  TX_TEEN_CLASSROOM_MANSFIELD = "dMWLhB4MR5WD0xLeV_PLpQ",
  TX_TEEN_CLASSROOM_RICHMOND = "FKp0F3bHSGalrVin3qptWw",
  TX_TEEN_ONLINE = "121336584",
  TX_TEEN_PARENT_ONLINE = "121336606",
  TX_ADULT_ONLINE = "119935239",
  TX_ADULT_CLASSROOM_RICHMOND = "eFo4IPHzRYm6tSMJhsSOVw",
  TX_TEEN_CLASSROOM_FTWORTH = "d4VHCktvRR2GGrSxF5hruw",
  TX_ADULT_CLASSROOM_FTWORTH = "bFmaz_bISJaqc250mhQkaQ",
  TX_TEEN_CLASSROOM_SUMMIT = "A-nHJ9pnQbi8E_esHSinGg",
}

export enum JobCode {
  AdministrativeClerical = "ACR",
  ClassroomInstruction = "CIR",
  CertifiedTraining = "CTR",
  NonTeaching = "NTR",
  Teaching = "TEACH",
  Training = "TRAIN",
}

export const JOB_CODE_DISPLAY: Record<JobCode, string> = {
  [JobCode.AdministrativeClerical]: "Administrative/Clerical",
  [JobCode.ClassroomInstruction]: "Classroom Instructor",
  [JobCode.CertifiedTraining]: "Certified Trainer",
  [JobCode.NonTeaching]: "Non-Teaching Rate",
  [JobCode.Teaching]: "Teaching Rate",
  [JobCode.Training]: "Training Rate",
}

export type ProductPackage = GQLProductPackage | GQLProductPackageWithPrice
export type ProductPackageWithPrices = GQLProductPackageWithPrice
